<template>
    <v-dialog v-model="dialog" width="700" persistent>
        <v-card class="rounded-lg" style="display: flex; flex-direction: column;">
            <v-card-title
                style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div>
                    <span class="font-weight-bold text-h6 text-white">{{ dialog_title }}</span>
                    <div class="text-caption text-white">{{ dialog_sub_title }}</div>
                </div>
                <v-btn v-if="!loading" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 200px);">
                <v-form ref="form" class="mt-2">
                    <v-row dense>
                        <v-col cols="12" v-if="action === 'edit'">
                            <div class="d-flex align-center ga-2 mb-2">
                                <span class="text-caption text-grey">Course Outcome Code:</span>
                                <v-chip color="darkGreen" variant="outlined" size="small" class="font-weight-bold">
                                    {{ code }}
                                </v-chip>
                            </div>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea
                                v-model="description"
                                :rules="[formRules.required]"
                                label="Description"
                                variant="outlined"
                                rounded="lg"
                                rows="3"
                                color="darkGreen">
                            </v-textarea>
                        </v-col>
                        <!-- Program Outcomes Alignment -->
                        <v-col cols="12">
                            <div class="text-subtitle-2 font-weight-bold mb-1">
                                Program Outcomes Alignment
                            </div>
                            <div class="text-caption text-grey mb-2">
                                Legend: &nbsp;<strong>I</strong> – Introductory &nbsp;|&nbsp;
                                <strong>E</strong> – Enabling &nbsp;|&nbsp;
                                <strong>D</strong> – Demonstrative &nbsp;|&nbsp;
                                Click the active button again to deselect.
                            </div>
                            <v-card variant="outlined" rounded="lg" class="pa-4" color="grey-lighten-4">
                                <div v-if="loadingPO" class="d-flex justify-center pa-4">
                                    <v-progress-circular indeterminate color="darkGreen" size="24"></v-progress-circular>
                                </div>
                                <div v-else-if="programOutcomes.length === 0" class="text-center text-caption text-grey pa-2">
                                    No program outcomes found for this program.
                                </div>
                                <div v-else class="d-flex flex-wrap ga-3">
                                    <div
                                        v-for="po in programOutcomes"
                                        :key="po.id"
                                        class="d-flex flex-column align-center"
                                        style="min-width: 72px;">
                                        <v-tooltip :text="po.code + ': ' + po.description" location="top" max-width="250">
                                            <template v-slot:activator="{ props }">
                                                <div
                                                    v-bind="props"
                                                    class="text-caption font-weight-bold mb-2 text-truncate text-black"
                                                    style="max-width: 72px; cursor: default;">
                                                    {{ po.code }}
                                                </div>
                                            </template>
                                        </v-tooltip>
                                        <div class="d-flex ga-1">
                                            <v-btn
                                                @click="toggleAlignment(po.code, 'I')"
                                                :variant="alignmentMap[po.code] === 'I' ? 'elevated' : 'outlined'"
                                                :color="alignmentMap[po.code] === 'I' ? 'blue' : 'grey'"
                                                size="x-small"
                                                min-width="24"
                                                class="px-1">
                                                I
                                            </v-btn>
                                            <v-btn
                                                @click="toggleAlignment(po.code, 'E')"
                                                :variant="alignmentMap[po.code] === 'E' ? 'elevated' : 'outlined'"
                                                :color="alignmentMap[po.code] === 'E' ? 'orange' : 'grey'"
                                                size="x-small"
                                                min-width="24"
                                                class="px-1">
                                                E
                                            </v-btn>
                                            <v-btn
                                                @click="toggleAlignment(po.code, 'D')"
                                                :variant="alignmentMap[po.code] === 'D' ? 'elevated' : 'outlined'"
                                                :color="alignmentMap[po.code] === 'D' ? 'green' : 'grey'"
                                                size="x-small"
                                                min-width="24"
                                                class="px-1">
                                                D
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>
                            </v-card>
                        </v-col>

                        <v-col cols="12">
                            <v-text-field
                                v-model="assessment_tools"
                                :rules="[formRules.required]"
                                label="Assessment Tools"
                                variant="outlined"
                                rounded="lg"
                                color="darkGreen">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="time_frame"
                                :rules="[formRules.required]"
                                label="Time Frame (e.g. Week 9)"
                                variant="outlined"
                                rounded="lg"
                                color="darkGreen">
                            </v-text-field>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-3">
                <v-spacer></v-spacer>
                <v-btn class="mr-1" :disabled="loading" text="Cancel" variant="outlined" elevation="1"
                    @click="closeDialog()"></v-btn>
                <v-btn class="mr-1" :loading="loading" text="Save" variant="elevated" elevation="1" color="darkGreen"
                    @click="validateForm()"></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'

export default defineComponent({
    emits: ['refresh'],
    data() {
        return {
            dialog: false,
            dialog_title: null,
            dialog_sub_title: null,
            action: null,
            loading: false,
            loadingPO: false,

            id: null,
            code: null,
            description: null,
            assessment_tools: null,
            time_frame: null,

            course: null,
            program: null,
            programOutcomes: [],
            alignmentMap: {},
        }
    },
    methods: {
        async initAdd(course, program) {
            this.dialog = true
            this.dialog_title = 'Course Learning Outcome'
            this.dialog_sub_title = 'Add new CLO for ' + course.code + ' - ' + course.title
            this.action = 'add'

            this.id = null
            this.code = null
            this.description = null
            this.assessment_tools = null
            this.time_frame = null
            this.course = course
            this.program = program
            this.alignmentMap = {}

            await this.loadProgramOutcomes(program)
        },
        async initEdit(data, course, program) {
            this.dialog = true
            this.dialog_title = 'Course Learning Outcome'
            this.dialog_sub_title = 'Update CLO for ' + course.code + ' - ' + course.title
            this.action = 'edit'

            this.id = data.id
            this.code = data.code
            this.description = data.description
            this.assessment_tools = data.assessment_tools
            this.time_frame = data.time_frame
            this.course = course
            this.program = program

            await this.loadProgramOutcomes(program)
            this.buildAlignmentMap(data.program_outcomes)
        },
        async loadProgramOutcomes(program) {
            this.loadingPO = true
            this.programOutcomes = []
            let res = await this.axiosCall(
                '/program-outcomes/findAllByProgramId/' + program.id + '/' + this.$store.state.semester_schoolyear.id,
                'get', null, true
            )
            if (res && res.data) {
                this.programOutcomes = res.data
                if (this.action === 'add') {
                    const map = {}
                    res.data.forEach(po => { map[po.code] = undefined })
                    this.alignmentMap = map
                }
            }
            this.loadingPO = false
        },
        buildAlignmentMap(existing) {
            const map = {}
            this.programOutcomes.forEach(po => {
                map[po.code] = (existing && existing[po.code]) ? existing[po.code] : undefined
            })
            this.alignmentMap = map
        },
        toggleAlignment(poCode, value) {
            const current = this.alignmentMap[poCode]
            this.alignmentMap = {
                ...this.alignmentMap,
                [poCode]: current === value ? undefined : value
            }
        },
        async validateForm() {
            const { showSnackbar } = useSnackbar()
            const { valid } = await this.$refs.form.validate()

            if (valid) {
                this.loading = true
                const cleanedAlignment = {}
                Object.keys(this.alignmentMap).forEach(key => {
                    if (this.alignmentMap[key]) {
                        cleanedAlignment[key] = this.alignmentMap[key]
                    }
                })

                const formData = {
                    description: this.description,
                    program_outcomes: cleanedAlignment,
                    assessment_tools: this.assessment_tools,
                    time_frame: this.time_frame,
                    semester_schoolyear: this.$store.state.semester_schoolyear.id,
                    course_code: this.course.code,
                }

                if (this.action === 'add') {
                    let res = await this.axiosCall('/course-outcomes', 'post', formData, true)
                    if (res && res.data && res.data.status === 200) {
                        this.loading = false
                        this.$emit('refresh')
                        showSnackbar('Course outcome successfully added!')
                        this.closeDialog()
                    } else {
                        this.loading = false
                    }
                } else if (this.action === 'edit') {
                    let res = await this.axiosCall('/course-outcomes/' + this.id, 'patch', formData, true)
                    if (res && res.data && res.data.status === 200) {
                        this.loading = false
                        this.$emit('refresh')
                        showSnackbar('Course outcome successfully updated!')
                        this.closeDialog()
                    } else {
                        this.loading = false
                    }
                }
            }
        },
        closeDialog() {
            this.$refs.form.reset()
            this.programOutcomes = []
            this.alignmentMap = {}
            this.dialog = false
        }
    }
})
</script>

<style scoped>
</style>
